"use client";

import { FAVORITES_COOKIE_NAME, parseFavoriteStore, type FavoriteStore } from "@/lib/marketsuite/favorites";
import { PropertyListingType } from "@/lib/types/marketsuite/datatypes/property";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

type FavoritesContextValue = {
	favorites: FavoriteStore;
	add: (id: string, listingType: PropertyListingType) => void;
	remove: (id: string, listingType: PropertyListingType) => void;
	toggle: (id: string, listingType: PropertyListingType) => void;
	has: (id: string, listingType: PropertyListingType) => boolean;
	getIdsByType: (listingType: PropertyListingType) => string[];
};

const FavoritesContext = createContext<FavoritesContextValue | undefined>(undefined);

function readCookieStore(): FavoriteStore {
	if (typeof document === "undefined") return {};

	const cookie = document.cookie.split("; ").find((cookie) => cookie.startsWith(`${FAVORITES_COOKIE_NAME}=`));

	return parseFavoriteStore(cookie?.slice(FAVORITES_COOKIE_NAME.length + 1));
}

function writeCookieStore(store: FavoriteStore) {
	if (typeof document === "undefined") return;

	const value = encodeURIComponent(JSON.stringify(store));

	document.cookie = `${FAVORITES_COOKIE_NAME}=${value};path=/;max-age=${COOKIE_MAX_AGE_SECONDS};SameSite=Lax`;
}

export function FavoritesProvider({ children }: { children: React.ReactNode }) {
	const [favorites, setFavorites] = useState<FavoriteStore>({});

	useEffect(() => {
		setFavorites(readCookieStore());
	}, []);

	const add = useCallback((id: string, listingType: PropertyListingType) => {
		setFavorites((prev) => {
			const ids = prev[listingType] ?? [];

			if (ids.includes(id)) return prev;

			const next = { ...prev, [listingType]: [...ids, id] };

			writeCookieStore(next);
			return next;
		});
	}, []);

	const remove = useCallback((id: string, listingType: PropertyListingType) => {
		setFavorites((prev) => {
			const ids = prev[listingType] ?? [];

			if (!ids.includes(id)) return prev;

			const nextIds = ids.filter((favoriteId) => favoriteId !== id);
			const next = { ...prev };

			if (nextIds.length) {
				next[listingType] = nextIds;
			} else {
				delete next[listingType];
			}

			writeCookieStore(next);
			return next;
		});
	}, []);

	const toggle = useCallback((id: string, importType: PropertyListingType) => {
		setFavorites((prev) => {
			const ids = prev[importType] ?? [];
			const isFavorite = ids.includes(id);
			const nextIds = isFavorite ? ids.filter((favoriteId) => favoriteId !== id) : [...ids, id];
			const next = { ...prev };

			if (nextIds.length) {
				next[importType] = nextIds;
			} else {
				delete next[importType];
			}

			writeCookieStore(next);
			return next;
		});
	}, []);

	const has = useCallback(
		(id: string, importType: PropertyListingType) => {
			return (favorites[importType] ?? []).includes(id);
		},
		[favorites],
	);

	const getIdsByType = useCallback(
		(listingType: PropertyListingType) => {
			return favorites[listingType] ?? [];
		},
		[favorites],
	);

	return <FavoritesContext.Provider value={{ favorites, add, remove, toggle, has, getIdsByType }}>{children}</FavoritesContext.Provider>;
}

export function useFavorites() {
	const context = useContext(FavoritesContext);

	if (context === undefined) {
		throw new Error("useFavorites must be used within a FavoritesProvider");
	}

	return context;
}
